import { useGet, usePatch } from "@albertine/api-client";
import {
    type LmtErrorResponse,
    type Tenant,
    TenantSchema,
    type Theme,
} from "@albertine/types/openapi/lmt";
import {
    dynamicThemeNameMaxLength,
    ThemePreference,
    ThemePreferenceTextMap,
} from "@albertine/types/theme";
import { zodResolver } from "@hookform/resolvers/zod";
import * as Sentry from "@sentry/react";
import { useRef } from "react";
import { type SubmitErrorHandler, type SubmitHandler, useForm } from "react-hook-form";
import { DynamicThemeFormSchema } from "../../common/types/DynamicTheme";
import { excludeUndefined } from "../../common/utils/array.util";
import { EventEmitter } from "../../EventEmitter";
import { useClaims } from "../../utils/claims.util";
import { scrollRefToTop } from "../../utils/window.util";
import SelectTags from "../CatalogBeta/SelectTags";
import Button from "../Common/Buttons";
import { Flex } from "../Common/Flex";
import { Form } from "../Common/Form/Form";
import Input from "../Common/Input";
import Label from "../Common/Label";
import SelectArticles from "../Common/SelectArticles";
import SelectLocations from "../Common/SelectLocations";
import Tag from "../Common/Tag";
import Toggle from "../Common/Toggle";
import { TitleValuePair } from "../MemberData/TitleValuePairs";
import "./ThemeForm.css";
import { type TenantTabKey, TenantTabs } from "../Common/TenantTabs";
import { Spinner } from "../ui/spinner";

function onSaveThemeError(error: LmtErrorResponse, theme: Theme) {
    Sentry.captureException(error, {
        extra: { message: `Failed to update theme ${theme.id}` },
    });

    EventEmitter.emit({
        name: "showToastMessage",
        params: {
            message: error.error ?? `Updating theme "${theme.name}" failed`,
            type: "error",
        },
    });
}

type DynamicThemeFormProps = {
    theme: Theme;
    onCancel: () => void;
    onSave: (theme: Theme) => void;
};
function DynamicThemeForm(props: DynamicThemeFormProps) {
    const { theme, onCancel, onSave } = props;

    const scrollElementRef = useRef<HTMLDivElement>(null);
    const { isAdmin } = useClaims();
    const { mutate: updateThemeMutation, isPending: loading } = usePatch(
        "updateTheme",
        { onSuccess: onSave, onError: (error) => onSaveThemeError(error, theme) },
    );

    const {
        getValues,
        setValue,
        handleSubmit,
        trigger,
        formState: { errors, isSubmitted, isValid },
    } = useForm<DynamicThemeFormSchema>({
        resolver: zodResolver(DynamicThemeFormSchema),
        defaultValues: {
            published: theme?.publishedAt || null,
        },
    });

    const onSubmit: SubmitHandler<DynamicThemeFormSchema> = (formValues) => {
        updateThemeMutation({
            param: { themeId: theme.id },
            json: {
                published: formValues.published !== null,
            },
        });
    };

    const onInvalid: SubmitErrorHandler<DynamicThemeFormSchema> = () => {
        trigger();
        scrollRefToTop(scrollElementRef, true);
    };

    const errorTexts = Object.values(errors)
        .map((it) => it.message)
        .filter(excludeUndefined);

    const themeName = (
        <Input.TextArea
            className="textarea__heading"
            placeholder="Name"
            value={theme.name}
            rows={1}
            maxLength={dynamicThemeNameMaxLength}
            autoResize
            trim
            removeLineBreaks
            disabled
        />
    );

    const tenantTabs = [
        {
            key: "default" as TenantTabKey,
            children: <TenantTabContent themeId={theme.id} tenant={undefined} />,
        },
        ...TenantSchema.options.map((tenant) => ({
            key: tenant,
            children: <TenantTabContent themeId={theme.id} tenant={tenant} />,
        })),
    ];

    const targeting = (
        <TitleValuePair
            title={<Label.Medium.SemiBold.N10>Targeting</Label.Medium.SemiBold.N10>}
            value={
                <Flex.Column gap="xlarge">
                    <Flex.Column gap="large">
                        <h5>Preferences</h5>
                        <SelectTags<ThemePreference>
                            values={ThemePreference.options.map((themePreference) => ({
                                id: themePreference,
                                value:
                                    ThemePreferenceTextMap[themePreference] ||
                                    themePreference,
                                selected: theme.preferences.includes(themePreference),
                            }))}
                            onChange={() => {}}
                            selectMultiple
                            size="medium"
                            disabled
                        />
                    </Flex.Column>

                    <Flex.Column gap="large">
                        <h5>Location</h5>

                        <Flex.Row
                            gap="medium"
                            className="theme-form__targeting-location--disabled"
                        >
                            {theme.location.length === 0 ? (
                                <>
                                    <Tag.Medium.Bold.P90>Global</Tag.Medium.Bold.P90>
                                    <Tag.Medium.Bold.N90>Local</Tag.Medium.Bold.N90>
                                </>
                            ) : (
                                <>
                                    <Tag.Medium.Bold.N90>Global</Tag.Medium.Bold.N90>
                                    <Tag.Medium.Bold.P90>Local</Tag.Medium.Bold.P90>
                                </>
                            )}
                        </Flex.Row>

                        <SelectLocations
                            initialValues={theme.location}
                            onChange={() => {}}
                            disabled
                        />
                    </Flex.Column>
                </Flex.Column>
            }
        />
    );

    const admin = (
        <TitleValuePair
            title={<Label.Medium.SemiBold.N10>Admin</Label.Medium.SemiBold.N10>}
            value={
                <Flex.Column gap="medium">
                    <Flex.Row gap="medium">
                        <h5>Visibility</h5>
                        <Toggle
                            checked={!!getValues("published")}
                            disabled={!isAdmin}
                            onChange={() => {
                                const value = getValues("published");

                                if (value) {
                                    setValue("published", null);
                                } else {
                                    setValue("published", new Date().toISOString());
                                }
                                trigger(["published"]);
                            }}
                        />

                        {getValues("published") ? (
                            <Tag.Medium.P90>Published</Tag.Medium.P90>
                        ) : (
                            <Tag.Medium.N80>Unpublished</Tag.Medium.N80>
                        )}
                    </Flex.Row>
                    <Label.Medium.Light.N10>
                        {getValues("published")
                            ? "This item can be discovered or promoted in World of Albertine"
                            : "This item can't be discovered or promoted in World of Albertine"}
                    </Label.Medium.Light.N10>
                </Flex.Column>
            }
        />
    );

    return (
        <Flex.Column className="theme-form">
            <Form.Base
                className="theme-form__form"
                onSubmit={handleSubmit(onSubmit, onInvalid)}
            >
                <Form.Inputs
                    className="theme-form__inputs"
                    ref={scrollElementRef}
                    disabled={loading}
                >
                    {!isValid && isSubmitted && <Form.Errors errors={errorTexts} />}

                    <Form.Spacer size="medium" />
                    <Flex.Column gap="xlarge" alignItems="start">
                        <Flex.Column gap="medium">
                            {themeName}
                            <Form.Spacer size="medium" />
                            <TenantTabs
                                defaultTabText="Default"
                                keepAllTabsRendered
                                tabs={tenantTabs}
                                tenantOptions={[]}
                            />
                        </Flex.Column>

                        <Form.Divider />

                        {targeting}

                        <Form.Spacer size="small" />
                        <Form.Divider />
                        {admin}
                        <Form.Spacer />
                    </Flex.Column>
                </Form.Inputs>

                <Form.Actions className="theme-form__actions">
                    <div>
                        <Flex.Row gap="medium">
                            <Button onClick={onCancel}>Cancel</Button>
                            <Button.N10
                                type="submit"
                                disabled={loading}
                                showLoadingSpinner={loading}
                            >
                                Save
                            </Button.N10>
                        </Flex.Row>
                    </div>
                </Form.Actions>
            </Form.Base>
        </Flex.Column>
    );
}

function TenantTabContent(props: { themeId: string; tenant?: Tenant }) {
    const { themeId, tenant } = props;

    const { data: articles, isLoading: isFetchingArticles } = useGet(
        "lmtCatalogThemeCatalogItems",
        {
            param: { themeId },
            query: {
                tenant,
            },
        },
    );

    if (isFetchingArticles) {
        return <Spinner />;
    }

    return (
        <SelectArticles
            initialArticles={articles?.catalogItems ?? []}
            onChange={() => {}}
            disabled
        />
    );
}

export default DynamicThemeForm;
